import { Component, computed, inject, model, viewChild } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Observable } from 'rxjs';
import { filter, map, shareReplay } from 'rxjs/operators';

import {
  BaseComponent,
  BaseFormComponent,
  FormlyService,
  FormsValidators,
  ModalService,
  TabFormModalComponent,
} from 'tms-lib';
import { QuickDespatchOutcome, QuickDespatchPack } from '../../../models';

/**
 * Quick despatch (QuickDespatchAndBookShipping), shipping = 'Packs': the despatch note has been
 * created and the carrier only needs each pack's weight and size. Save closes the modal WITH the
 * packs, which QuickDespatchHandlerService books; Back dismisses it, and the handler prints the
 * note without booking.
 *
 * ⚠️ ng-bootstrap renders the modal BEFORE the caller can set `outcome` (ui.md, "Opening a modal"),
 * so the fields and model are derived from the signal rather than built in ngOnInit.
 */
@Component({
  selector: 'app-shipping-packs-modal',
  templateUrl: './shipping-packs-modal.component.html',
  styleUrl: './shipping-packs-modal.component.scss',
  imports: [TabFormModalComponent, BaseFormComponent],
})
export class ShippingPacksModalComponent extends BaseComponent {
  private readonly formlyService = inject(FormlyService);
  private readonly modalService = inject(ModalService);

  private readonly form = viewChild(BaseFormComponent);

  public readonly outcome = model<QuickDespatchOutcome>();

  // A copy of the packs for the form to edit - Formly writes into it in place.
  protected readonly formModel = computed(() => ({
    packs: (this.outcome()?.packs ?? []).map((pack: QuickDespatchPack) => ({ ...pack })),
  }));

  protected readonly fields = computed(() => this.buildFields(this.outcome()?.packs ?? []));

  // Stable field (not a getter), carrying the despatch note's real id for app-summary-top.
  protected readonly model$: Observable<{ id: string }> = toObservable(this.outcome).pipe(
    filter((outcome: QuickDespatchOutcome) => !!outcome?.despatchNoteId),
    map((outcome: QuickDespatchOutcome) => ({ id: outcome.despatchNoteId })),
    shareReplay(1),
  );

  protected save() {
    if (!this.form()?.validate()) {
      return;
    }

    const packs: QuickDespatchPack[] = this.formModel().packs.map((pack: QuickDespatchPack) => ({
      ...pack,
      grossWeight: Number(pack.grossWeight) || 0,
      length: Number(pack.length) || 0,
      width: Number(pack.width) || 0,
      height: Number(pack.height) || 0,
    }));
    this.modalService.close(packs);
  }

  private buildFields(packs: QuickDespatchPack[]): FormlyFieldConfig[] {
    // Not flatMap: tms-lib's type-check reaches this file and its older `lib` target has no
    // flatMap (TS2550).
    const rows = packs.map((_pack: QuickDespatchPack, index: number) =>
      this.formlyService.twoColumnGrid(
        [
          this.formlyService.textReadonly(`packs.${index}.shippingPackKey`, $localize`Shipping Pack`),
          this.formlyService.textReadonly(`packs.${index}.shippingPackDescription`, $localize`Description`),
          this.formlyService.number(
            `packs.${index}.grossWeight`,
            $localize`Gross Weight`,
            { required: true },
            undefined,
            undefined,
            [FormsValidators.number, FormsValidators.greaterThan(0)],
          ),
        ],
        [
          this.formlyService.number(`packs.${index}.length`, $localize`Length`),
          this.formlyService.number(`packs.${index}.width`, $localize`Width`),
          this.formlyService.number(`packs.${index}.height`, $localize`Height`),
        ],
      ),
    );
    return ([] as FormlyFieldConfig[]).concat(...rows);
  }
}
